// Integrator — converts ExternalInsight domain edges into DependencyGraph GraphEdges.

import type { DependencyGraph } from "../dependency/types.js";
import type { EdgeKind, GraphEdge } from "../types.js";
import type {
  AnchorMapping,
  ExternalEdge,
  ExternalEvidence,
  ExternalInsight,
  PropagationDirection,
  UnresolvedEdge,
} from "./types.js";

const DEFAULT_WEIGHT = 0.7;

const CALL_KINDS = new Set([
  "calls",
  "reads_column",
  "uses_model",
  "consumes",
  "api_operation_dependency",
]);
const TYPE_KINDS = new Set([
  "implements",
  "type_reference",
  "satisfies",
  "maps_to",
]);

export function resolveEdgeKind(externalKind: string): EdgeKind {
  if (CALL_KINDS.has(externalKind)) return "calls";
  if (TYPE_KINDS.has(externalKind)) return "type_reference";
  return "depends_on";
}

export interface IntegrateInsightResult {
  edges: GraphEdge[];
  unresolvedEdges: UnresolvedEdge[];
}

interface AnchorIndex {
  symbolNodes: Map<string, string[]>;
  fileNodes: Map<string, string[]>;
}

/**
 * Build lookups from domainId → symbol ids and domainId → file paths.
 */
function buildAnchorIndex(mappings: AnchorMapping[] | undefined): AnchorIndex {
  const symbolNodes = new Map<string, string[]>();
  const fileNodes = new Map<string, string[]>();
  if (!mappings) return { symbolNodes, fileNodes };

  for (const mapping of mappings) {
    fileNodes.set(mapping.domainId, mapping.filePaths);

    const symbols: string[] = [];
    if (mapping.symbols) {
      symbols.push(...mapping.symbols.map((s) => s.symbolId));
    }
    if (mapping.symbolIds) {
      symbols.push(...mapping.symbolIds.filter((id) => !symbols.includes(id)));
    }
    if (symbols.length > 0) {
      symbolNodes.set(mapping.domainId, symbols);
    }
  }
  return { symbolNodes, fileNodes };
}

function resolveNodes(
  domainId: string,
  index: AnchorIndex,
  edgeEvidence?: ExternalEvidence[],
): { nodes: string[]; isSymbol: boolean } | undefined {
  if (edgeEvidence) {
    const evidenceSymbols = edgeEvidence
      .filter((e) => e.symbolId)
      .map((e) => e.symbolId!);
    if (evidenceSymbols.length > 0) {
      return { nodes: evidenceSymbols, isSymbol: true };
    }
  }

  const symbols = index.symbolNodes.get(domainId);
  if (symbols && symbols.length > 0) {
    return { nodes: symbols, isSymbol: true };
  }

  const files = index.fileNodes.get(domainId);
  if (files && files.length > 0) {
    return { nodes: files, isSymbol: false };
  }

  return undefined;
}

function orientedPairs(
  fromNodes: string[],
  toNodes: string[],
  propagation: PropagationDirection | undefined,
): Array<{ from: string; to: string }> {
  const forward = fromNodes.flatMap((from) =>
    toNodes.map((to) => ({ from, to })),
  );

  const direction = propagation ?? "forward";
  if (direction === "forward") return forward;

  const backward = toNodes.flatMap((from) =>
    fromNodes.map((to) => ({ from, to })),
  );

  if (direction === "backward") return backward;

  // "both" — dedupe identical pairs when endpoints overlap
  const seen = new Set<string>();
  const pairs: Array<{ from: string; to: string }> = [];
  for (const p of [...forward, ...backward]) {
    const key = `${p.from}\0${p.to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    pairs.push(p);
  }
  return pairs;
}

function externalEdgeToGraphEdge(
  from: string,
  to: string,
  edge: ExternalEdge,
  source: string,
): GraphEdge {
  const weight = edge.weight ?? DEFAULT_WEIGHT;
  const detailParts = [
    `source=${source}`,
    `external_kind=${edge.kind}`,
    `domain_from=${edge.from}`,
    `domain_to=${edge.to}`,
  ];
  if (edge.propagation && edge.propagation !== "forward") {
    detailParts.push(`propagation=${edge.propagation}`);
  }
  if (edge.metadata && Object.keys(edge.metadata).length > 0) {
    detailParts.push(`metadata=${JSON.stringify(edge.metadata)}`);
  }

  return {
    from,
    to,
    kind: resolveEdgeKind(edge.kind),
    classification: "candidate",
    evidence: [
      {
        type: "tool_output_field_similarity",
        detail: detailParts.join("; "),
        confidence: weight,
      },
    ],
    relevanceScore: weight,
  };
}

/**
 * Convert a single {@link ExternalInsight} into graph-ready edges.
 * Unresolved domain endpoints are logged and collected, not thrown.
 */
export function integrateInsight(insight: ExternalInsight): IntegrateInsightResult {
  const anchorIndex = buildAnchorIndex(insight.anchorMapping);
  const edges: GraphEdge[] = [];
  const unresolvedEdges: UnresolvedEdge[] = [];

  for (const extEdge of insight.edges) {
    const fromResolved = resolveNodes(
      extEdge.from,
      anchorIndex,
      extEdge.evidence,
    );
    const toResolved = resolveNodes(extEdge.to, anchorIndex, extEdge.evidence);

    if (!fromResolved || !toResolved) {
      const missing: string[] = [];
      if (!fromResolved) missing.push(`from="${extEdge.from}"`);
      if (!toResolved) missing.push(`to="${extEdge.to}"`);
      const reason = `No AnchorMapping for ${missing.join(", ")}`;
      console.warn(
        `[external-insight] unresolved edge (${insight.source}): ${extEdge.from} -> ${extEdge.to} (${reason})`,
      );
      unresolvedEdges.push({
        from: extEdge.from,
        to: extEdge.to,
        kind: extEdge.kind,
        source: insight.source,
        reason,
      });
      continue;
    }

    const propagation = extEdge.propagation ?? "forward";
    const pairs = orientedPairs(
      fromResolved.nodes,
      toResolved.nodes,
      propagation,
    );
    for (const { from, to } of pairs) {
      edges.push(externalEdgeToGraphEdge(from, to, extEdge, insight.source));
    }
  }

  return { edges, unresolvedEdges };
}

/** Integrate multiple insights and merge unresolved edge lists. */
export function integrateInsights(
  insights: ExternalInsight[],
): IntegrateInsightResult {
  const edges: GraphEdge[] = [];
  const unresolvedEdges: UnresolvedEdge[] = [];

  for (const insight of insights) {
    const result = integrateInsight(insight);
    edges.push(...result.edges);
    unresolvedEdges.push(...result.unresolvedEdges);
  }

  return { edges, unresolvedEdges };
}
